import { Button, FormControl, FormLabel, HStack, Input, Text } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkHealthcareInstitutionIdDuplicate, uploadFile } from "../../../api";
import { colors } from "../../../styles";
import { ISignUpPharmacy } from "../../../types";
import { getApiErrorCode } from "../../../utils";
import { getSignUpErrorMessage } from "./errors";
import FileAttach from "./FileAttach";
import StepLayout, { FieldMessage, underlineInputProps } from "./StepLayout";

interface IPharmacyInfoProps {
    progress: number;
    setPharmacyData: React.Dispatch<React.SetStateAction<Partial<ISignUpPharmacy>>>;
    onNext: () => void;
}

interface IPharmacyForm {
    name: string;
    address: string;
    addressDetail: string;
    bizRegNo: string;
    healthcareInstitutionId: string;
}

const healthcareInstitutionIdRegex = /^\d{8}$/;
const bizRegNoRegex = /^\d{3}-?\d{2}-?\d{5}$/;

/* 개국 약사 전용. Pharmacy 객체(약국 정보 + 관련 서류)를 만든다. */

export default function PharmacyInfo({ progress, setPharmacyData, onNext }: IPharmacyInfoProps) {

    /* 로딩 상태, 에러 메시지, 디바운스된 요양기관 번호 */

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [businessRegistrationCertImage, setBusinessRegistrationCertImage] = useState<File | null>(null);
    const [establishmentRegistrationCertImage, setEstablishmentRegistrationCertImage] = useState<File | null>(null);
    const [debouncedHealthcareInstitutionId, setDebouncedHealthcareInstitutionId] = useState("");

    /* useForm */

    const { register, handleSubmit, watch, setValue } = useForm<IPharmacyForm>();
    const name = watch("name") ?? "";
    const address = watch("address") ?? "";
    const addressDetail = watch("addressDetail") ?? "";
    const bizRegNo = watch("bizRegNo") ?? "";
    const healthcareInstitutionId = watch("healthcareInstitutionId") ?? "";
    const isBizRegNoFormatValid = bizRegNoRegex.test(bizRegNo);
    const isHealthcareInstitutionIdFormatValid = healthcareInstitutionIdRegex.test(healthcareInstitutionId);

    /* 디바운스된 요양기관 번호 */

    useEffect(() => {
        const id = setTimeout(() => setDebouncedHealthcareInstitutionId(healthcareInstitutionId), 400);
        return () => clearTimeout(id);
    }, [healthcareInstitutionId]);

    /* 요양기관 번호 중복 확인 쿼리 */

    const {
        isLoading: isCheckingHealthcareInstitutionId,
        isError: isHealthcareInstitutionIdCheckError,
        error: healthcareInstitutionIdCheckError,
    } = useQuery({
        queryKey: ["checkHealthcareInstitutionIdDuplicate", debouncedHealthcareInstitutionId],
        queryFn: () => checkHealthcareInstitutionIdDuplicate({ healthcareInstitutionId: debouncedHealthcareInstitutionId }),
        enabled: !!debouncedHealthcareInstitutionId && healthcareInstitutionIdRegex.test(debouncedHealthcareInstitutionId),
        retry: false,
    });

    /* 요양기관 번호 중복 확인 결과 */

    const isHealthcareInstitutionIdDuplicate =
        isHealthcareInstitutionIdCheckError &&
        getApiErrorCode(healthcareInstitutionIdCheckError) === "HEALTHCARE_INSTITUTION_ALREADY_USED";

    const isHealthcareInstitutionIdCheckUnavailable =
        isHealthcareInstitutionIdCheckError && !isHealthcareInstitutionIdDuplicate;

    /* 요양기관 번호 중복 확인 뮤테이션 */

    const checkHealthcareInstitutionIdDuplicateMutation = useMutation({
        mutationFn: checkHealthcareInstitutionIdDuplicate,
        retry: false,
    });

    /* 증명 서류 업로드 뮤테이션 */

    const uploadFileMutation = useMutation({
        mutationFn: uploadFile,
    });

    /* 주소 찾기 버튼 클릭 핸들러 */

    const onFindAddressBtnClick = () => {
        new window.daum.Postcode({
            oncomplete: (data: { address: string }) => {
                setValue("address", data.address);
            },
        }).open();
    };

    /* 폼 제출 핸들러 */

    const onSubmit = async (data: IPharmacyForm) => {
        if (!businessRegistrationCertImage || !establishmentRegistrationCertImage) {
            setErrorMessage("약국 관련 증명 서류를 모두 첨부해주세요.");
            return;
        }

        /* 로딩 상태, 에러 메시지 설정 */

        setIsLoading(true);
        setErrorMessage("");

        try {

            /* 요양기관 번호 중복 확인 */

            await checkHealthcareInstitutionIdDuplicateMutation.mutateAsync({
                healthcareInstitutionId: data.healthcareInstitutionId,
            });

            /* 증명 서류 업로드 */

            const businessRegistrationCertImageUrl = await uploadFileMutation.mutateAsync({
                file: businessRegistrationCertImage,
            });

            const establishmentRegistrationCertImageUrl = await uploadFileMutation.mutateAsync({
                file: establishmentRegistrationCertImage,
            });

            /* 약국 정보와 관련 서류 URL을 Pharmacy 객체로 저장 */

            setPharmacyData({
                name: `${data.name}약국`, /* 입력값은 상호명만 받고, 제출 시 '약국'을 붙인다. */
                address: `${data.address} ${data.addressDetail}`.trim(),
                bizRegNo: data.bizRegNo,
                healthcareInstitutionId: data.healthcareInstitutionId,
                businessRegistrationCertImage: businessRegistrationCertImageUrl,
                establishmentRegistrationCertImage: establishmentRegistrationCertImageUrl,
            });

            /* 다음 화면으로 이동 */

            onNext();
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "약국 정보 확인 중 문제가 발생했습니다."));
        } finally {
            setIsLoading(false);
        }
    };

    const bizRegNoHint = (() => {
        if (!bizRegNo) return null;
        if (!isBizRegNoFormatValid) {
            return { message: "사업자등록번호는 XXX-XX-XXXXX 형식으로 입력해주세요.", tone: "error" as const };
        }
        return null;
    })();

    /* 요양기괸 번호 힌트 */

    const healthcareInstitutionHint = (() => {
        if (!healthcareInstitutionId) return null;
        if (!isHealthcareInstitutionIdFormatValid) {
            return { message: "요양기관 번호는 8자리 숫자로 입력해주세요.", tone: "error" as const };
        }
        if (isCheckingHealthcareInstitutionId) {
            return { message: "중복 확인 중...", tone: "muted" as const };
        }
        if (isHealthcareInstitutionIdDuplicate) {
            return { message: "이미 사용 중인 요양기관 번호입니다.", tone: "error" as const };
        }
        if (isHealthcareInstitutionIdCheckUnavailable) {
            return { message: "요양기관 번호 중복 확인에 실패했습니다.", tone: "error" as const };
        }
        return { message: "사용 가능한 요양기관 번호입니다.", tone: "success" as const };
    })();

    /* 폼 채우기 여부 */

    const isFormFilled =
        Boolean(name.trim()) &&
        Boolean(address.trim()) &&
        Boolean(addressDetail.trim()) &&
        isBizRegNoFormatValid &&
        isHealthcareInstitutionIdFormatValid &&
        Boolean(businessRegistrationCertImage) &&
        Boolean(establishmentRegistrationCertImage);

    /* render */

    return (
        <StepLayout
            progress={progress}
            titles={["약국 정보를 입력해주세요."]}
            asForm
            onSubmit={handleSubmit(onSubmit)}
        >

            {/* 약국 이름 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>약국 이름</FormLabel>
                <HStack>
                    <Input
                        {...register("name", { required: true })}
                        type="text"
                        placeholder="약국 이름"
                        {...underlineInputProps}
                    />
                    <Text fontSize={16} fontWeight={700} color={colors.g1} marginLeft={2} whiteSpace="nowrap">
                        약국
                    </Text>
                </HStack>
            </FormControl>

            {/* 주소 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>주소</FormLabel>
                <HStack width="100%">
                    <Input
                        {...register("address", { required: true })}
                        type="text"
                        readOnly
                        placeholder="주소 입력"
                        {...underlineInputProps}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        color={colors.g1}
                        size="sm"
                        onClick={onFindAddressBtnClick}
                        marginLeft={2}
                    >
                        주소 찾기
                    </Button>
                </HStack>
            </FormControl>

            {/* 상세 주소 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>상세 주소</FormLabel>
                <Input
                    {...register("addressDetail", { required: true })}
                    type="text"
                    placeholder="상세 주소"
                    {...underlineInputProps}
                />
            </FormControl>

            {/* 사업자등록번호 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>사업자등록번호</FormLabel>
                <Input
                    {...register("bizRegNo", { required: true })}
                    type="text"
                    placeholder="000-00-00000"
                    {...underlineInputProps}
                />
                <FieldMessage message={bizRegNoHint?.message} tone={bizRegNoHint?.tone} />
            </FormControl>

            {/* 요양기관 번호 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>요양기관 번호</FormLabel>
                <Input
                    {...register("healthcareInstitutionId", { required: true })}
                    type="text"
                    placeholder="요양기관 번호"
                    {...underlineInputProps}
                />
                <FieldMessage message={healthcareInstitutionHint?.message} tone={healthcareInstitutionHint?.tone} />
            </FormControl>

            {/* 증명 서류 업로드 폼 */}

            <FormControl marginTop={8}>
                <FormLabel fontSize="sm" color={colors.blue} marginBottom={5}>증명 서류</FormLabel>
                <FileAttach
                    label="01 | 사업자 등록증"
                    inputId="business-upload"
                    file={businessRegistrationCertImage}
                    onChange={(file) => {
                        setBusinessRegistrationCertImage(file);
                        setErrorMessage("");
                    }}
                />
                <FileAttach
                    label="02 | 약국 개설 등록증"
                    inputId="establishment-upload"
                    file={establishmentRegistrationCertImage}
                    onChange={(file) => {
                        setEstablishmentRegistrationCertImage(file);
                        setErrorMessage("");
                    }}
                    showDivider
                />
            </FormControl>

            {/* 에러 메시지 */}

            <FieldMessage message={errorMessage} />

            {/* 다음 버튼 */}

            <Button
                type="submit"
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={10}
                isLoading={isLoading}
                disabled={
                    !isFormFilled ||
                    isCheckingHealthcareInstitutionId ||
                    isHealthcareInstitutionIdCheckError
                }
            >
                <Text fontSize={16} fontWeight={700}>다음</Text>
            </Button>
        </StepLayout>
    );
}
